import type { ColumnDef } from "@tanstack/react-table";
import type { DataTableColumnMeta } from "@/components/shared/data-table/DataTable";
import { Checkbox } from "@/components/ui/checkbox";

const MOBILE_CHECKBOX_HIT_AREA =
  "relative max-md:before:absolute max-md:before:-inset-[14px] max-md:before:content-['']";

interface SelectionColumnOptions<TData> {
  /** Row-level checkbox name, e.g. `(invoice) => \`invoice ${invoice.uid}\``. */
  getRowLabel: (row: TData) => string;
  /** Header checkbox name, e.g. "Select all invoices". */
  selectAllLabel: string;
}

/**
 * The leading checkbox column, wired to the table's own selection state.
 *
 * Selection is TanStack-owned so `data-state="selected"` row styling, the
 * header's indeterminate state, and the ids a bulk action bar reads all come
 * from one source. Features spread this in rather than hand-rolling a third
 * copy of the same two checkboxes.
 */
export function selectionColumn<TData>({
  getRowLabel,
  selectAllLabel,
}: SelectionColumnOptions<TData>): ColumnDef<TData, unknown> {
  return {
    cell: ({ row }) => (
      <Checkbox
        aria-label={`Select ${getRowLabel(row.original)}`}
        checked={row.getIsSelected()}
        className={MOBILE_CHECKBOX_HIT_AREA}
        disabled={!row.getCanSelect()}
        onCheckedChange={(checked) => row.toggleSelected(checked === true)}
      />
    ),
    enableHiding: false,
    header: ({ table }) => (
      <Checkbox
        aria-label={selectAllLabel}
        checked={selectAllState(table.getIsAllRowsSelected(), table.getIsSomeRowsSelected())}
        className={MOBILE_CHECKBOX_HIT_AREA}
        onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
      />
    ),
    id: "select",
    meta: {
      containsControls: true,
      hideable: false,
      mobileRole: "selection",
      width: "w-12",
    } satisfies DataTableColumnMeta,
  };
}

function selectAllState(allSelected: boolean, someSelected: boolean): boolean | "indeterminate" {
  if (allSelected) {
    return true;
  }
  return someSelected ? "indeterminate" : false;
}

/** Ids of the currently selected rows, in table order. */
export function selectedRowIds(rowSelection: Record<string, boolean>): string[] {
  return Object.keys(rowSelection).filter((id) => rowSelection[id]);
}

/**
 * The id TanStack will use for a column definition: its explicit `id`, else
 * its `accessorKey`. Mirrors TanStack's own resolution so the visibility menu
 * and the table agree on what a column is called.
 */
export function getDataTableColumnId<TData>(column: ColumnDef<TData, unknown>): string | undefined {
  if (column.id) {
    return column.id;
  }
  const accessorKey = (column as { accessorKey?: unknown }).accessorKey;
  return typeof accessorKey === "string" ? accessorKey : undefined;
}

/**
 * A column's visibility-menu label: `meta.label` when set, otherwise a plain
 * string header. Anything else — a rendered header, an empty actions header —
 * has no name to show and is therefore not offered as hideable.
 */
function getDataTableColumnLabel<TData>(column: ColumnDef<TData, unknown>): string | undefined {
  const meta = column.meta as DataTableColumnMeta | undefined;
  if (meta?.label) {
    return meta.label;
  }
  return typeof column.header === "string" && column.header.length > 0 ? column.header : undefined;
}

/** Columns a user is allowed to hide, in declaration order. */
export function hideableColumns<TData>(
  columns: ColumnDef<TData, unknown>[],
): { id: string; label: string }[] {
  const options: { id: string; label: string }[] = [];

  for (const column of columns) {
    const meta = column.meta as DataTableColumnMeta | undefined;
    if (meta?.hideable === false || column.enableHiding === false) {
      continue;
    }
    const id = getDataTableColumnId(column);
    const label = getDataTableColumnLabel(column);
    if (id && label) {
      options.push({ id, label });
    }
  }

  return options;
}
